import React, { useState } from 'react';
import { KeyRound, X, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Self-service password change button + modal for any logged-in dealer/manager/admin.
 * Reuses POST /api/dealer-auth/change-my-password (requires current + new password).
 */
export default function ChangeMyPasswordButton({ token, label = 'Сменить пароль' }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setCurrent('');
    setNext('');
    setConfirmPwd('');
    setShowCurrent(false);
    setShowNext(false);
  };

  const close = () => {
    if (saving) return;
    setOpen(false);
    reset();
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!current || !next) {
      toast.error('Заполните оба поля');
      return;
    }
    if (next !== confirmPwd) {
      toast.error('Пароли не совпадают');
      return;
    }
    if (next.length < 8 || !/[A-Za-zА-Яа-я]/.test(next) || !/\d/.test(next)) {
      toast.error('Новый пароль: минимум 8 символов, буквы и цифры');
      return;
    }
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/dealer-auth/change-my-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ current_password: current, new_password: next }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || 'Не удалось сменить пароль');
      toast.success('Пароль обновлён. Используйте новый пароль при следующем входе.');
      setOpen(false);
      reset();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 text-white/40 hover:text-white text-sm transition-colors border border-white/10 px-3 py-1.5 rounded-lg hover:border-white/30"
        data-testid="change-my-password-btn"
        title={label}
      >
        <KeyRound className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">{label}</span>
      </button>

      {open && (
        <div
          data-testid="change-my-password-modal"
          className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={close}
        >
          <form
            onSubmit={submit}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl p-6"
          >
            <button
              type="button"
              onClick={close}
              disabled={saving}
              className="absolute top-3 right-3 text-[#061d36]/40 hover:text-[#061d36] p-1 rounded"
              aria-label="Закрыть"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 rounded-full bg-[#8b4444]/10 flex items-center justify-center">
                <KeyRound className="w-5 h-5 text-[#8b4444]" />
              </div>
              <h3 className="font-bold text-[#061d36]">Смена пароля</h3>
            </div>

            <div className="space-y-3">
              <PasswordField
                label="Текущий пароль"
                value={current}
                onChange={setCurrent}
                show={showCurrent}
                toggleShow={() => setShowCurrent(s => !s)}
                testid="my-pwd-current"
                autoFocus
              />
              <PasswordField
                label="Новый пароль (мин. 8 символов, буквы + цифры)"
                value={next}
                onChange={setNext}
                show={showNext}
                toggleShow={() => setShowNext(s => !s)}
                testid="my-pwd-new"
              />
              <PasswordField
                label="Повторите новый пароль"
                value={confirmPwd}
                onChange={setConfirmPwd}
                show={showNext}
                toggleShow={() => setShowNext(s => !s)}
                testid="my-pwd-confirm"
              />

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={close}
                  disabled={saving}
                  className="flex-1 bg-[#061d36]/5 text-[#061d36] rounded-lg py-2.5 font-medium hover:bg-[#061d36]/10 transition-colors"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  data-testid="my-pwd-save"
                  className="flex-1 bg-[#8b4444] text-white rounded-lg py-2.5 font-semibold hover:bg-[#7a3838] transition-colors disabled:opacity-50"
                >
                  {saving ? 'Сохранение…' : 'Сменить пароль'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

function PasswordField({ label, value, onChange, show, toggleShow, testid, autoFocus }) {
  return (
    <div>
      <label className="text-xs text-[#061d36]/70 font-medium mb-1 block">{label}</label>
      <div className="relative">
        <input
          autoFocus={autoFocus}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 border border-[#061d36]/15 rounded-lg focus:outline-none focus:border-[#8b4444] text-sm font-mono pr-10"
          data-testid={testid}
        />
        <button
          type="button"
          onClick={toggleShow}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-[#061d36]/40 hover:text-[#061d36] p-1"
          aria-label={show ? 'Скрыть' : 'Показать'}
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}
